import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useMemo
} from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';

// --- ESTADO INICIAL ATUALIZADO ---
const initialState = {
  user: null,
  accessToken: null, // Renomeado de 'token'
  refreshToken: null, // Adicionado
  isAuthenticated: false,
  loading: true,
  error: null,
  contratos: [],
  selectedContrato: null
};

const authActions = {
  SET_LOADING: 'SET_LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  REFRESH_ACCESS_TOKEN: 'REFRESH_ACCESS_TOKEN', // Nova ação
  SET_SELECTED_CONTRATO: 'SET_SELECTED_CONTRATO',
  CLEAR_ERROR: 'CLEAR_ERROR' // Adicionado para limpar erros ao sair da página
};

// --- REDUCER ATUALIZADO ---
const authReducer = (state, action) => {
  switch (action.type) {
    case authActions.SET_LOADING:
      return { ...state, loading: action.payload };
    case authActions.LOGIN_SUCCESS: {
      const { user, accessToken, refreshToken, contratos, selectedContrato } = action.payload;
      return {
        ...state,
        user,
        accessToken,
        refreshToken,
        contratos,
        ...(selectedContrato !== undefined ? { selectedContrato } : {}),
        isAuthenticated: true,
        loading: false,
        error: null
      };
    }
    case authActions.LOGIN_FAILURE:
      return { ...initialState, loading: false, error: action.payload };
    case authActions.LOGOUT:
      return { ...initialState, loading: false };
    case authActions.REFRESH_ACCESS_TOKEN:
      return { ...state, accessToken: action.payload };
    case authActions.SET_SELECTED_CONTRATO:
      return { ...state, selectedContrato: action.payload };
    default:
      return state;
  }
};

const pickFreshContrato = (contratos, storedJson) => {
  if (!Array.isArray(contratos) || contratos.length === 0) return null;

  if (storedJson) {
    try {
      const stored = JSON.parse(storedJson);
      const fresh = contratos.find(
        (c) => String(c?.numero) === String(stored?.numero)
      );
      if (fresh) return fresh;
    } catch {
      // localStorage inválido — cai no primeiro contrato
    }
  }

  return contratos[0];
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // --- LOGOUT ATUALIZADO ---
  const logout = useCallback(() => {
    localStorage.removeItem('santa-casa-accessToken');
    localStorage.removeItem('santa-casa-refreshToken');
    localStorage.removeItem('santa-casa-selected-contrato');
    delete api.defaults.headers.common['Authorization'];
    delete api.defaults.headers.common['x-contract-number'];
    dispatch({ type: authActions.LOGOUT });
    toast.info('Você foi desconectado');
  }, []);

  // Efeito para verificar a autenticação inicial
  useEffect(() => {
    const checkAuth = async () => {
      dispatch({ type: authActions.SET_LOADING, payload: true });
      try {
        const accessToken = localStorage.getItem('santa-casa-accessToken');
        const refreshToken = localStorage.getItem('santa-casa-refreshToken');

        if (!accessToken || !refreshToken) {
          dispatch({ type: authActions.SET_LOADING, payload: false });
          return;
        }

        // Valida o accessToken para obter os dados do usuário
        const response = await api.post('/auth/validate-token');

        if (response.data.success) {
          const { user, contratos } = response.data.data;
          // Usa o contrato da API (situacao atualizada), não o snapshot do localStorage
          const storedContrato = localStorage.getItem('santa-casa-selected-contrato');
          const contractToSelect = pickFreshContrato(contratos, storedContrato);
          if (contractToSelect) {
            localStorage.setItem('santa-casa-selected-contrato', JSON.stringify(contractToSelect));
          }

          dispatch({
            type: authActions.LOGIN_SUCCESS,
            payload: {
              accessToken,
              refreshToken,
              user,
              contratos,
              selectedContrato: contractToSelect
            }
          });
        } else {
          logout();
        }
      } catch (error) {
        const status = error.response?.status;
        // Só desloga em falha REAL de autenticação. Para 401, o interceptor já
        // tentou (e falhou) o refresh; para 403 o refresh token é inválido.
        const isAuthError = status === 401 || status === 403;

        if (isAuthError) {
          logout();
        } else {
          // Erro transitório (rede, 429 rate limit, 5xx, servidor indisponível):
          // não conseguimos validar agora, mas os tokens podem continuar válidos.
          // Mantém os tokens no localStorage para a sessão se recuperar no próximo
          // F5/requisição, em vez de derrubar o usuário.
          console.warn('Não foi possível validar a sessão agora (erro transitório):', error.message);
        }
      } finally {
        dispatch({ type: authActions.SET_LOADING, payload: false });
      }
    };

    checkAuth();
  }, [logout]);

  // --- LOGIN ATUALIZADO ---
  // NÃO usa state.loading aqui: PublicRoute/ProtectedRoute desmontam a página
  // inteira quando loading=true, e o modal de situacao se perde.
  const login = useCallback(async (documento, contrato) => {
    const buildSituacaoResult = (data, status = 500) => {
      const situacao = data?.situacao || null;
      const message = data?.message || data?.mensagem || 'Não foi possível realizar o login.';
      dispatch({ type: authActions.LOGIN_FAILURE, payload: message });
      return {
        success: false,
        message,
        status,
        isNetworkError: false,
        situacao
      };
    };

    try {
      const response = await api.post('/auth/login', { documento, contrato });
      const data = response.data || {};

      if (data.situacao) {
        return buildSituacaoResult(data, response.status);
      }

      if (data.success) {
        const { accessToken, refreshToken, user, contratos } = data.data;

        localStorage.setItem('santa-casa-accessToken', accessToken);
        localStorage.setItem('santa-casa-refreshToken', refreshToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        
        const firstContract = contratos?.length > 0 ? contratos[0] : null;
        if (firstContract) {
          localStorage.setItem('santa-casa-selected-contrato', JSON.stringify(firstContract));
        }

        dispatch({
          type: authActions.LOGIN_SUCCESS,
          payload: {
            accessToken,
            refreshToken,
            user,
            contratos,
            selectedContrato: firstContract
          }
        });

        return { success: true, contratos };
      }

      return {
        success: false,
        message: data.message || 'Ocorreu um erro. Tente novamente.',
        status: response.status,
        isNetworkError: false,
        situacao: null
      };
    } catch (error) {
      console.error("Erro no Login:", error.response || error);
      
      let message = 'Ocorreu um erro. Tente novamente.';
      let status = null;
      let isNetworkError = false;
      let situacao = null;
      
      if (!error.response) {
        isNetworkError = true;
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          message = 'Tempo de conexão esgotado. Verifique sua conexão com a internet e tente novamente.';
        } else if (error.message?.includes('Network Error') || error.message?.includes('Failed to fetch')) {
          message = 'Verifique se você está conectado à internet e tente novamente.';
        } else {
          message = 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet e tente novamente.';
        }
      } else {
        status = error.response?.status;
        const data = error.response?.data || {};
        situacao = data.situacao || null;

        if (situacao) {
          return buildSituacaoResult(data, status);
        }

        if (status === 403) {
          message = data.message || 'Documento ou contrato inválido.';
        } else if (data.errors) {
          message = data.errors.map(e => e.msg).join(', ');
        } else if (data.message) {
          message = data.message;
        }
      }
      
      dispatch({ type: authActions.LOGIN_FAILURE, payload: message });
      
      if (isNetworkError) {
        toast.error(message, { autoClose: 6000 });
      } else {
        toast.error(message);
      }
      
      return { success: false, message, status, isNetworkError, situacao };
    }
  }, []);
  
  const selectContrato = useCallback((contrato) => {
    if (contrato && contrato.numero !== state.selectedContrato?.numero) {
        localStorage.setItem('santa-casa-selected-contrato', JSON.stringify(contrato));
        dispatch({ type: authActions.SET_SELECTED_CONTRATO, payload: contrato });
        toast.info(`Visualizando dados do contrato ${contrato.plano} (${contrato.numero}).`);
    }
  }, [state.selectedContrato]);

  const clearError = useCallback(() => {
    dispatch({ type: authActions.CLEAR_ERROR });
  }, []);

  // O objeto 'value' é memoizado para evitar re-renderizações desnecessárias
  const value = useMemo(() => ({
    ...state,
    login,
    logout,
    selectContrato,
    clearError,
    dispatch // Expondo o dispatch para uso no interceptor
  }), [state, login, logout, selectContrato, clearError]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};